import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { useAuthStore } from '@/shared/stores/auth.store'
import { getLastActivityAt } from '@/shared/api/client'
import { useToast } from '@/shared/components/ui/toast'
import { useAnnouncementModalStore } from '@/features/announcements/store'
import { TOURS, pageTourFor } from '../lib/tours'
import { onTourEnd, useWalkthroughStore } from '../stores/walkthrough.store'
import { useTourContext } from '../hooks/use-tour-context'
import { appDialogOpen, loadLocalOnboarding, WELCOME_SETTLE_MS } from '../utils/onboarding'
import { loadActive } from '../utils/progress-storage'
import {
  INVITE_GIVE_UP_MS,
  MAX_PAGE_CARD_SESSIONS,
  answeredStatus,
  decideInvite,
  eligibleInviteSteps,
  focusInField,
  inviteKindFor,
  loadLocalPages,
  loadSession,
  saveLocalPage,
  saveSession,
} from '../utils/page-invites'
import { locallyOptedOut, optOutOfPageInvites, recordPageStatus } from '../utils/page-status-write'
import type { Tour } from '../types'
import { isImpersonating } from '../utils/impersonation'
import { ENGINE_COPY } from '../utils/engine-copy'
import { RichCopy } from './rich-copy'

const RECHECK_MS = 1000
const UNAVAILABLE = '[data-tour-page-unavailable], [data-access-denied]'

/**
 * Progressive page onboarding. On the first visit to a page that has a guide,
 * either OFFER it with a small non-modal card ("New to X?") or light a passive
 * beacon on the ? button (pages edited in place). Never starts a tour itself.
 * Mounted once by the WalkthroughHost.
 */
export function PageInviteCard() {
  const { pathname } = useLocation()
  const user = useAuthStore((s) => s.user)
  const ctx = useTourContext()
  const tourRunning = useWalkthroughStore((s) => s.activeTourId !== null)
  const startTour = useWalkthroughStore((s) => s.startTour)
  const setBeaconTourId = useWalkthroughStore((s) => s.setBeaconTourId)
  const announcementOpen = useAnnouncementModalStore((s) => s.openId !== null)
  const { addToast } = useToast()

  const userId = user?.id ?? null
  const tour = pageTourFor(pathname)
  /** Keyed by user: a card shown to one identity (an admin) never reappears for another (the impersonated user). */
  const [shown, setShown] = useState<{ userId: string; tourId: string; path: string; steps: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  /** Tours hidden for the rest of this page visit (interaction / gave up). */
  const hiddenForVisit = useRef(new Set<string>())

  // Latest facts for the timer.
  const live = useRef({ user, ctx, tourRunning, announcementOpen })
  useEffect(() => {
    live.current = { user, ctx, tourRunning, announcementOpen }
  })

  // ── Page tours record their own outcome (overview never touches page tours) ──
  useEffect(() => {
    if (!userId) return
    return onTourEnd((e) => {
      // ANY tour ending starts the card cooldown: no card right after a tour (M16-1).
      saveSession(userId, { ...loadSession(userId), lastCardAt: Date.now() })
      if (!e.tourId.startsWith('page:')) return
      const t = TOURS.find((x) => x.id === e.tourId)
      if (!t) return
      if (e.outcome === 'completed') void recordPageStatus(userId, t.id, 'completed', t.version)
      else if (e.outcome === 'dismissed') void recordPageStatus(userId, t.id, 'dismissed', t.version)
      // aborted (navigation, access lost, logout) leaves 'started' if it was started from a card
    })
  }, [userId])

  // ── A visit: evaluate each second (≤ 30 s), offer a card or light the beacon ──
  useEffect(() => {
    // (A card from the previous page is hidden by `shown.path !== pathname`.)
    hiddenForVisit.current.clear()
    setBeaconTourId(null)
    if (!userId || !tour) return
    const kind = inviteKindFor(tour)
    if (kind === 'off') return
    const enteredAt = Date.now()
    let timer: ReturnType<typeof setTimeout>
    // A visit made by a running tour (the overview navigating) never offers a
    // card, even after that tour ends; the next real visit does (M16-1).
    let duringTour = live.current.tourRunning

    const check = () => {
      const { user: u, ctx: c, tourRunning: running, announcementOpen: annOpen } = live.current
      if (u?.id !== userId) return
      duringTour ||= running
      if (duringTour && kind === 'card') return
      const now = Date.now()
      const steps = eligibleInviteSteps(tour, c, TOURS, pathname)
      const first = steps.find((s) => s.target && !s.route && !s.activate)
      const { status, carryOver } = answeredStatus(tour.id, u.onboarding?.tours, userId)
      if (carryOver) void recordPageStatus(userId, tour.id, carryOver, tour.version) // lazy carry-over
      const session = loadSession(userId)
      const local = loadLocalPages(userId)[tour.id]
      const counted = session.counted?.includes(tour.id) ?? false

      const decision = decideInvite({
        kind,
        status,
        eligibleSteps: steps.length,
        // Fail closed: only an explicit server `true` (false while impersonating / opted out).
        pageInvites: u.onboarding?.page_invites === true && !locallyOptedOut(userId),
        impersonating: isImpersonating(u),
        welcomePending:
          (u.onboarding?.welcome_eligible === true && !loadLocalOnboarding(userId).answered) ||
          !!document.querySelector('.mc-welcome:not(.mc-invite)'),
        tourRunning: running,
        resumePending: !!loadActive(userId),
        now,
        lastCardAt: session.lastCardAt,
        lastActivityAt: getLastActivityAt(),
        focusInField: focusInField(document.activeElement),
        dialogOpen: appDialogOpen() || annOpen,
        helpMenuOpen: !!document.querySelector('[role="menu"]'),
        pageUnavailable: !!document.querySelector(UNAVAILABLE),
        firstTargetPresent: !first?.target || !!document.querySelector(first.target),
        settled: now - enteredAt >= WELCOME_SETTLE_MS,
        dirty: !!document.querySelector('[data-tour-dirty="true"]'),
        pristineOnly: !!tour.invitePristineOnly,
      })

      if (decision === 'beacon') setBeaconTourId(tour.id)
      else setBeaconTourId(null)

      if (decision === 'card' && !hiddenForVisit.current.has(tour.id)) {
        // Shown (unanswered) in MAX sessions already → record dismissed instead of nagging.
        if (!counted && (local?.views ?? 0) >= MAX_PAGE_CARD_SESSIONS) {
          void recordPageStatus(userId, tour.id, 'dismissed', tour.version)
          return
        }
        if (!counted) {
          saveLocalPage(userId, tour.id, { views: (local?.views ?? 0) + 1 })
          saveSession(userId, { ...session, counted: [...(session.counted ?? []), tour.id], lastCardAt: now })
        } else {
          saveSession(userId, { ...session, lastCardAt: now })
        }
        setShown({ userId, tourId: tour.id, path: pathname, steps: steps.length })
        return // shown: stop polling this visit (the card watches for its own exits)
      }
      if (now - enteredAt < INVITE_GIVE_UP_MS || decision === 'beacon') timer = setTimeout(check, RECHECK_MS)
    }
    timer = setTimeout(check, RECHECK_MS)
    return () => clearTimeout(timer)
  }, [pathname, userId, tour, setBeaconTourId])

  // ── While shown: hide on a tour starting, a blocker appearing, access dropping ──
  const visible =
    !!shown &&
    shown.userId === userId &&
    !isImpersonating(user) &&
    shown.path === pathname &&
    !tourRunning &&
    !announcementOpen &&
    !!tour &&
    tour.id === shown.tourId

  useEffect(() => {
    if (!shown || !tour) return
    const id = setInterval(() => {
      const steps = eligibleInviteSteps(tour, live.current.ctx, TOURS, pathname)
      const u = live.current.user
      const answered = !!u?.onboarding?.tours?.[tour.id]
      if (steps.length < 2 || answered || appDialogOpen() || (tour.invitePristineOnly && document.querySelector('[data-tour-dirty="true"]'))) {
        hiddenForVisit.current.add(tour.id)
        setShown(null)
      }
    }, RECHECK_MS)
    return () => clearInterval(id)
  }, [shown, tour, pathname])

  // Pristine-only pages (chart review): the card leaves the moment the user starts working.
  useEffect(() => {
    if (!visible || !tour?.invitePristineOnly) return
    const onWork = (e: Event) => {
      const target = e.target as Element | null
      if (target?.closest?.('.mc-invite')) return
      // Tab / Shift+Tab move focus (possibly into the card): not "starting work".
      if (e instanceof KeyboardEvent && (e.key === 'Tab' || e.key === 'Shift')) return
      hiddenForVisit.current.add(tour.id)
      setShown(null) // counts as a view (already counted), not a dismissal
    }
    document.addEventListener('pointerdown', onWork, true)
    document.addEventListener('keydown', onWork, true)
    return () => {
      document.removeEventListener('pointerdown', onWork, true)
      document.removeEventListener('keydown', onWork, true)
    }
  }, [visible, tour])

  // An anchored card (chart) follows its anchor on resize / pane changes.
  const [, setLayoutTick] = useState(0)
  useEffect(() => {
    if (!visible || !tour?.inviteAnchor) return
    const bump = () => setLayoutTick((n) => n + 1)
    window.addEventListener('resize', bump)
    const el = document.querySelector(tour.inviteAnchor)
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null
    if (el) ro?.observe(el)
    return () => {
      window.removeEventListener('resize', bump)
      ro?.disconnect()
    }
  }, [visible, tour])

  if (!visible || !tour || !userId || !shown) return null

  const answer = async (choice: 'start' | 'dismiss' | 'optout') => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    try {
      setShown(null)
      hiddenForVisit.current.add(tour.id)
      if (choice === 'start') {
        startTour(tour.id, 'invite')
        await recordPageStatus(userId, tour.id, 'started', tour.version)
      } else if (choice === 'dismiss') {
        await recordPageStatus(userId, tour.id, 'dismissed', tour.version)
        addToast({ variant: 'info', title: ENGINE_COPY.toast.laterTitle, description: ENGINE_COPY.toast.inviteLater })
      } else {
        await optOutOfPageInvites(userId)
        addToast({ variant: 'info', title: ENGINE_COPY.toast.optOutTitle, description: ENGINE_COPY.toast.optOut })
      }
      document.querySelector<HTMLElement>('[data-tour="header.help"]')?.focus()
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  return (
    <section
      className="mc-welcome mc-invite"
      role="region"
      aria-labelledby="mc-invite-title"
      aria-live="polite"
      style={anchorStyle(tour)}
    >
      <button type="button" className="mc-welcome-close" aria-label="Dismiss page tour suggestion" onClick={() => void answer('dismiss')} disabled={busy}>
        <X size={14} aria-hidden="true" />
      </button>
      <h2 id="mc-invite-title" className="mc-welcome-title">
        <RichCopy text={ENGINE_COPY.invite.title(tour.title)} />
      </h2>
      <p className="mc-welcome-body">{ENGINE_COPY.invite.body(shown.steps)}</p>
      <div className="mc-welcome-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void answer('start')} disabled={busy}>
          Start tour
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void answer('dismiss')} disabled={busy}>
          Not now
        </button>
      </div>
      <button type="button" className="mc-invite-optout" onClick={() => void answer('optout')} disabled={busy}>
        Don't suggest page tours
      </button>
    </section>
  )
}

/** Over `tour.inviteAnchor` (e.g. the chart's source pane) instead of under the header. */
function anchorStyle(tour: Tour): React.CSSProperties | undefined {
  if (!tour.inviteAnchor) return undefined
  const el = document.querySelector(tour.inviteAnchor)
  if (!el) return undefined
  const r = el.getBoundingClientRect()
  return { top: Math.max(r.top + 16, 0), left: r.left + 16, right: 'auto' }
}
